import React from 'react';
import { IProduct } from '../../Api/Models/Products';
import { useLocalization } from '../Helpers/useLocalization';

interface IPriceQuantityNoticeProps {
    product?: IProduct;
    // False when the shopper cannot see pricing at all, in which case there is no price to qualify.
    canSeePricing?: boolean;
    // 'full' is the explanatory sentence for a product page; 'short' is the compact form for a listing
    // card, which has no room for it. Same source number either way, so the two cannot disagree.
    variant?: 'full' | 'short';
    className?: string;
    style?: React.CSSProperties;
}

// AC-987: the storefront shows the cheapest vendor's price, but only that vendor's stock can be bought at
// it. Past that the order moves to a vendor holding more, and the cart legitimately costs more per unit
// than the listing showed. Saying so up front is the whole point of this component: without it the shopper
// meets the higher price for the first time in their cart, which reads as a bug.
//
// Renders nothing unless the BFF supplied a ceiling. It only does so when a larger quantity genuinely
// costs more, so ordinary products stay untouched. The number is derived server-side beside GetPrice —
// deliberately no pricing arithmetic here, since two copies of that logic drifting apart was AC-986.
const PriceQuantityNotice: React.FC<IPriceQuantityNoticeProps> = ({ product, canSeePricing, variant = 'full', className, style }) => {
    const L = useLocalization();
    const ceiling = product?.priceAppliesUpToQuantity;

    if (!canSeePricing || product?.hidePrice) return null;
    if (ceiling === undefined || ceiling === null || ceiling <= 0) return null;

    const quantity = ceiling.toLocaleString();
    const key = variant === 'short' ? 'localization_price_quantity_ceiling_short' : 'localization_price_quantity_ceiling';
    // Global replace: the copy is merchant-editable, so {quantity} may legitimately appear more than once.
    const text = L(key).replace(/\{quantity\}/g, quantity);
    // The card shows the compact wording, so give it the full sentence on hover rather than losing it.
    const title = variant === 'short'
        ? L('localization_price_quantity_ceiling').replace(/\{quantity\}/g, quantity)
        : undefined;

    // A block-displayed span rather than <p>: this is dropped into whatever container a surface happens
    // to have, and a span is valid in both flow and phrasing contexts. It also carries no UA margin, so
    // callers stop having to zero one out.
    return (
        <span className={className} style={{ display: 'block', fontStyle: 'italic', ...style }} title={title}>{text}</span>
    );
};

export default PriceQuantityNotice;
